import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { AlertCircle, ArrowLeft, Building2, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const API_URL = process.env.REACT_APP_API_URL || "";
const PLATFORM_URL = (process.env.REACT_APP_PUBLIC_URL || window.location.origin).replace(/\/+$/, "");
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const COMMENT_MAX = 1000;

type Role = "building" | "provider";
type Step = "loading" | "form" | "submitting" | "success";

interface Prefill {
  person: { givenName: string | null; familyName: string | null };
  company: { registryCode: string; name: string; address: string | null };
  registry: { outcome: "ok" | "not_found" | "unavailable"; email: string | null; phone: string | null };
}

/**
 * Access-request page (ticket 6): a guest picked «register / join as …» on the
 * role page. E-mail prefilled from the company's registry profile, a comment,
 * «Отправить». On success the Smart-ID session is over — the answer comes by
 * e-mail.
 */
export function AccessRequestPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();

  const registryCode = searchParams.get("registryCode") || "";
  const roleParam = searchParams.get("role");
  const role: Role | null = roleParam === "building" || roleParam === "provider" ? roleParam : null;

  const [step, setStep] = useState<Step>("loading");
  const [data, setData] = useState<Prefill | null>(null);
  const [email, setEmail] = useState("");
  const [comment, setComment] = useState("");
  const [error, setError] = useState("");
  const [result, setResult] = useState<{ emailOutcome: string } | null>(null);

  useEffect(() => {
    if (!registryCode || !role) {
      navigate("/select-role", { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get<{ success: boolean; data: Prefill }>(
          `${API_URL}/auth/smartid/access-request/prefill/${registryCode}`,
          { withCredentials: true }
        );
        if (cancelled) return;
        setData(res.data.data);
        setEmail(res.data.data.registry.email ?? "");
        setStep("form");
      } catch (err: any) {
        if (cancelled) return;
        const status = err.response?.status;
        if (status === 401) {
          navigate("/login", { replace: true });
          return;
        }
        toast.error(errorText(t, err.response?.data?.code));
        navigate("/select-role", { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [registryCode, role]);

  const emailValid = EMAIL_RE.test(email.trim());
  const canSubmit = step === "form" && emailValid && comment.length <= COMMENT_MAX;

  const handleSubmit = async () => {
    if (!canSubmit || !role) return;
    setStep("submitting");
    setError("");
    try {
      const res = await axios.post<{ success: boolean; data: { id: string; emailOutcome: string } }>(
        `${API_URL}/auth/smartid/access-request`,
        { registryCode, role, email: email.trim(), comment: comment.trim() || undefined, locale: i18n.language?.slice(0, 2) },
        { withCredentials: true }
      );
      setResult(res.data.data);
      setStep("success");
    } catch (err: any) {
      const code = err.response?.data?.code as string | undefined;
      if (err.response?.status === 401) {
        navigate("/login", { replace: true });
        return;
      }
      setError(errorText(t, code));
      setStep("form");
    }
  };

  const personName = data ? [data.person.givenName, data.person.familyName].filter(Boolean).join(" ") : "";
  const roleLabel = role === "provider" ? t("accessRequest.roleProvider") : t("accessRequest.roleBuilding");
  const emailSent = result ? ["ACCEPTED", "LOGGED"].includes(result.emailOutcome) : false;

  return (
    <div className="min-h-screen flex">
      <div className="flex-1 bg-card px-4 sm:px-6 py-5 flex flex-col">
        <Link to="/" className="w-fit group flex items-center gap-2">
          <img src="/logo.png" alt="Unikort" className="h-9 w-auto" />
          <span className="text-xl font-semibold text-[#5756d6] dark:text-[#777aff]">Unikort</span>
        </Link>

        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-[560px]">
            {(step === "loading" || step === "submitting") && (
              <div className="flex flex-col items-center py-16 gap-4">
                <Loader2 className="w-10 h-10 text-primary animate-spin" />
              </div>
            )}

            {step === "form" && data && (
              <>
                <h1 className="text-2xl font-semibold mb-1">{t("accessRequest.title")}</h1>
                <p className="text-sm text-muted-foreground mb-6">{t("accessRequest.subtitle")}</p>

                <div className="flex items-start gap-3 p-4 rounded-xl border border-border bg-background mb-6">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-foreground">{data.company.name}</div>
                    {data.company.address && (
                      <div className="text-sm text-muted-foreground mt-0.5 truncate">{data.company.address}</div>
                    )}
                    <div className="text-xs text-muted-foreground mt-1">
                      {data.company.registryCode} · {roleLabel}
                    </div>
                  </div>
                </div>

                {personName && (
                  <p className="text-sm text-muted-foreground mb-6">
                    {t("accessRequest.personLabel")}: <span className="font-medium text-foreground">{personName}</span>
                  </p>
                )}

                <div className="mb-6">
                  <Label htmlFor="ar-email" className="text-sm font-medium mb-2 block">
                    {t("accessRequest.emailLabel")} *
                  </Label>
                  <Input id="ar-email" type="email" value={email} maxLength={254} onChange={(e) => setEmail(e.target.value)} required />
                  <p className="text-xs text-muted-foreground mt-1">
                    {data.registry.outcome === "unavailable"
                      ? t("accessRequest.registryUnavailable")
                      : data.registry.email
                      ? t("accessRequest.emailFromRegistry")
                      : t("accessRequest.emailNotInRegistry")}
                  </p>
                </div>

                <div className="mb-6">
                  <Label htmlFor="ar-comment" className="text-sm font-medium mb-2 block">
                    {t("accessRequest.commentLabel")}
                  </Label>
                  <textarea
                    id="ar-comment"
                    value={comment}
                    maxLength={COMMENT_MAX}
                    rows={4}
                    onChange={(e) => setComment(e.target.value)}
                    className="w-full px-3 py-2 border border-input rounded-md bg-background text-sm"
                  />
                  <p className="text-xs text-muted-foreground mt-1">{t("accessRequest.commentHint")}</p>
                </div>

                {error && (
                  <div className="bg-destructive/10 text-destructive text-sm p-4 rounded-xl border border-destructive/20 mb-4 flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 mt-0.5" />
                    {error}
                  </div>
                )}

                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={() => navigate("/select-role")}>
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    {t("accessRequest.back")}
                  </Button>
                  <Button className="flex-1" onClick={handleSubmit} disabled={!canSubmit}>
                    {t("accessRequest.submit")}
                  </Button>
                </div>
              </>
            )}

            {step === "success" && (
              <div className="flex flex-col items-center py-12 gap-4 text-center">
                <CheckCircle2 className="w-12 h-12 text-green-600" />
                <h1 className="text-2xl font-semibold">{t("accessRequest.successTitle")}</h1>
                <p className="text-sm text-muted-foreground max-w-[420px]">
                  {emailSent
                    ? t("accessRequest.successText", { email: email.trim() })
                    : t("accessRequest.successNoEmail", { email: email.trim() })}
                </p>
                <p className="text-sm text-muted-foreground">
                  {t("accessRequest.platformLine")} <span className="font-medium text-foreground">{PLATFORM_URL}</span>
                </p>
                <Button className="mt-4" onClick={() => navigate("/login", { replace: true })}>
                  {t("accessRequest.toLogin")}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const KNOWN_ERRORS = new Set([
  "request_pending", "request_rejected", "request_approved", "too_many_requests", "whitelisted",
  "not_your_company", "reauthenticate", "invalid_email", "invalid_body",
]);

function errorText(t: (key: string) => string, code: string | undefined): string {
  return t(code && KNOWN_ERRORS.has(code) ? `accessRequest.errors.${code}` : "accessRequest.errors.generic");
}
